// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext } from '../app';
import { el } from '../dom';
import { formatMass } from '../format';
import { divisionColor } from '../colors';
import { squarify } from '../utils/squarify';

export function renderIndustries(root: HTMLElement, ctx: AppContext) {
  root.appendChild(el('h1', { class: 'section-title' }, 'Industries'));
  root.appendChild(el('p', { class: 'section-sub' }, [
    'Emissions rolled up by ',
    el('span', { class: 'glossary-link', 'data-term': 'anzsic', tabindex: '0' }, 'ANZSIC'),
    ' industry division and subdivision.'
  ]));

  const { divisions, subdivisions } = ctx.data.industries;
  const total = divisions.reduce((acc, d) => acc + d.total, 0);

  // Division leaderboard (treemap-style)
  const treemap = el('div', { class: 'card mb-lg' });
  treemap.appendChild(el('h2', { class: 'card-title' }, 'Treemap — division share of lifetime emissions'));
  treemap.appendChild(el('p', { class: 'card-subtitle' }, 'Box area is proportional to total kg released. Hover for exact totals.'));
  treemap.appendChild(buildTreemap(divisions, total));
  root.appendChild(treemap);

  // Division leaderboard table
  const lead = el('div', { class: 'card mb-lg' });
  lead.appendChild(el('h2', { class: 'card-title' }, 'Division leaderboard'));
  lead.appendChild(el('p', { class: 'card-subtitle' }, 'Split by air / water / land.'));
  const wrap = el('div', { class: 'table-wrap' });
  const max = divisions[0]?.total ?? 1;
  const t = el('table', { class: 'data' }, [
    el('thead', null, [el('tr', null, [
      el('th', null, '#'),
      el('th', null, 'Division'),
      el('th', null, 'Air'),
      el('th', null, 'Water'),
      el('th', null, 'Land'),
      el('th', null, 'Total'),
      el('th', { style: 'width:120px' }, '')
    ])]),
    el('tbody', null, divisions.map((d, i) => el('tr', null, [
      el('td', { class: 'num' }, String(i + 1)),
      el('td', null, [
        el('span', { class: 'legend-swatch', style: `background:${divisionColor(d.code)}` }),
        d.name
      ]),
      el('td', { class: 'num' }, formatMass(d.air)),
      el('td', { class: 'num' }, formatMass(d.water)),
      el('td', { class: 'num' }, formatMass(d.land)),
      el('td', { class: 'num' }, formatMass(d.total)),
      el('td', null, [bar(d.total / max, divisionColor(d.code))])
    ])))
  ]);
  wrap.appendChild(t);
  lead.appendChild(wrap);
  root.appendChild(lead);

  // Subdivision drill-down
  const sub = el('div', { class: 'card' });
  sub.appendChild(el('h2', { class: 'card-title' }, 'Top 20 subdivisions'));
  sub.appendChild(el('p', { class: 'card-subtitle' }, 'Where within each division does the bulk of the emissions actually sit?'));
  const subWrap = el('div', { class: 'table-wrap' });
  const subMax = subdivisions[0]?.total ?? 1;
  const subT = el('table', { class: 'data' }, [
    el('thead', null, [el('tr', null, [
      el('th', null, '#'),
      el('th', null, 'Subdivision'),
      el('th', null, 'Division'),
      el('th', null, 'Total'),
      el('th', { style: 'width:120px' }, '')
    ])]),
    el('tbody', null, subdivisions.slice(0, 20).map((s, i) => el('tr', null, [
      el('td', { class: 'num' }, String(i + 1)),
      el('td', null, s.name),
      el('td', null, [
        el('span', { class: 'legend-swatch', style: `background:${divisionColor(s.division_code)}` }),
        s.division_name
      ]),
      el('td', { class: 'num' }, formatMass(s.total)),
      el('td', null, [bar(s.total / subMax, divisionColor(s.division_code))])
    ])))
  ]);
  subWrap.appendChild(subT);
  sub.appendChild(subWrap);
  root.appendChild(sub);
}

// Squarified treemap (canonical layout from src/utils/squarify).
function buildTreemap(divisions: import('../types').Division[], total: number): HTMLElement {
  const W = 920, H = 460;
  const wrap = document.createElement('div');
  const ns = 'http://www.w3.org/2000/svg';
  const svgEl = document.createElementNS(ns, 'svg');
  svgEl.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svgEl.setAttribute('class', 'treemap');
  svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  // Convert to value list and squarify (divisions arrive sorted desc by total)
  const items = divisions.filter(d => d.total > 0).map(d => ({
    code: d.code,
    name: d.name,
    value: d.total,
    color: divisionColor(d.code)
  }));
  if (items.length === 0) {
    return wrap;
  }
  const rects = squarify(items.map(i => i.value), W, H);
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    const it = items[i];
    const rect = document.createElementNS(ns, 'rect');
    rect.setAttribute('x', String(r.x));
    rect.setAttribute('y', String(r.y));
    rect.setAttribute('width', String(r.w));
    rect.setAttribute('height', String(r.h));
    rect.setAttribute('fill', it.color);
    const tip = `${it.name} · ${formatMass(it.value)} · ${((it.value / total) * 100).toFixed(1)}%`;
    rect.setAttribute('data-tip', tip);
    rect.setAttribute('aria-label', tip);
    svgEl.appendChild(rect);

    // Labels for larger boxes
    if (r.w > 80 && r.h > 36) {
      const text = document.createElementNS(ns, 'text');
      text.setAttribute('x', String(r.x + 8));
      text.setAttribute('y', String(r.y + 18));
      text.setAttribute('font-size', '12');
      text.setAttribute('font-weight', '600');
      text.setAttribute('fill', '#fff');
      text.textContent = shorten(it.name, Math.floor(r.w / 7));
      svgEl.appendChild(text);
      const sub = document.createElementNS(ns, 'text');
      sub.setAttribute('x', String(r.x + 8));
      sub.setAttribute('y', String(r.y + 34));
      sub.setAttribute('font-size', '11');
      sub.setAttribute('fill', '#ffffffd0');
      sub.textContent = formatMass(it.value);
      svgEl.appendChild(sub);
    }
  }
  wrap.appendChild(svgEl);
  return wrap;
}

function shorten(s: string, max: number): string {
  return s.length > max ? s.slice(0, Math.max(4, max - 1)) + '…' : s;
}

function bar(frac: number, color: string): HTMLElement {
  const w = Math.max(2, Math.min(100, frac * 100));
  return el('div', { style: 'background: var(--bg-elevated); border-radius: 4px; height: 8px; overflow: hidden;' }, [
    el('div', { style: `background: ${color}; height: 100%; width: ${w}%;` })
  ]);
}
